import React from 'react';
import {OrderFormProps } from './types';
import { ArcAlert, ArcButton, ArcOutlinedInput, ArcStack, ArcText } from '@src/core/ui';
import { useNavigate } from 'react-router-dom';
import { Controller } from 'react-hook-form';
import { REQUIRED_VALIDATION_TEXT } from '@src/core/config';
import { useDispatch, useSelector } from 'react-redux';
import { setMapCenter } from '@src/core/controllers';
import { ApiErrorResponse, LocationEntity } from '@src/core/entities';
import { GoogleSearchInput } from '@src/core/components';
import { routesConfig } from '@src/core/routes';
import { selectReadOnlyTakeOff, setReadOnlyTakeOff, setTakeOffLocation, setDestinationLocation, selectTakeOffLocation, selectDestinationLocation } from '@src/features/order/pages/upsert-order-page/redux';
import { useCheckAndPlanMutation } from '@src/core/features';
import { getGeocode } from 'use-places-autocomplete';

const COORDINATE_INPUT_PATTERN = /^-?\d*\.?\d*$/;
const GEOCODE_DEBOUNCE_MS = 500;

const isCoordinateInput = (value: string) => COORDINATE_INPUT_PATTERN.test(value);

const validateCoordinate = (
  value: string | number,
  min: number,
  max: number,
) => {
  if (value === '' || value == null) return false;
  const n = Number(value);
  return Number.isFinite(n) && n >= min && n <= max;
};

const hasValidCoordinatePair = (
  latitude: string | number,
  longitude: string | number,
) => (
  validateCoordinate(latitude, -90, 90) &&
  validateCoordinate(longitude, -180, 180)
);

const OrderForm: React.FC<OrderFormProps> = (props) => {
  const { form, isSubmitting, isCanceling, onSubmit, goBack } = props;

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const readOnly = useSelector(selectReadOnlyTakeOff);
  const takeOffLocation = useSelector(selectTakeOffLocation);
  const destinationLocation = useSelector(selectDestinationLocation);

  const [checkAndPlan, { error }] = useCheckAndPlanMutation();
  const destinationLatitude = form.watch('destinationLocation.latitude');
  const destinationLongitude = form.watch('destinationLocation.longitude');

  const onChangeTakeOff = (location: LocationEntity) => {
    const { latitude, longitude } = location;
    form.setValue('takeOffLocation', location);
    dispatch(setTakeOffLocation(location));
    dispatch(setMapCenter({ lat: Number(latitude), lng: Number(longitude) }));
  };

  const onChangeDestination = (location:  LocationEntity) => {
    const { latitude, longitude } = location;
    form.setValue('destinationLocation', location);
    dispatch(setDestinationLocation(location));
    dispatch(setMapCenter({ lat: Number(latitude), lng: Number(longitude) }));
  };

  const onChangeReadOnly = (value: boolean) => {
    dispatch(setReadOnlyTakeOff(value));
  };

  const onDestinationCoordinateChange = (
    key: 'latitude' | 'longitude',
    value: string,
    onChange: (value: string) => void,
  ) => {
    if (!isCoordinateInput(value)) return;

    onChange(value);

    const current = form.getValues('destinationLocation');
    const nextLocation: LocationEntity = {
      address: current?.address ?? destinationLocation?.address ?? '',
      latitude: current?.latitude ?? destinationLocation?.latitude ?? '',
      longitude: current?.longitude ?? destinationLocation?.longitude ?? '',
      [key]: value,
    };

    dispatch(setDestinationLocation(nextLocation));

    if (hasValidCoordinatePair(nextLocation.latitude, nextLocation.longitude)) {
      dispatch(setMapCenter({
        lat: Number(nextLocation.latitude),
        lng: Number(nextLocation.longitude),
      }));
    }
  };

  React.useEffect(() => {
    const lat = Number(destinationLatitude);
    const lng = Number(destinationLongitude);
    if (!validateCoordinate(destinationLatitude, -90, 90)) return;
    if (!validateCoordinate(destinationLongitude, -180, 180)) return;

    const timer = window.setTimeout(() => {
      getGeocode({ location: { lat, lng } })
        .then((addresses) => {
          const address = addresses[0]?.formatted_address;
          if (!address) return;

          const current = form.getValues('destinationLocation');
          const nextLocation: LocationEntity = {
            ...current,
            address,
            latitude: destinationLatitude,
            longitude: destinationLongitude,
          };

          form.setValue('destinationLocation.address', address, {
            shouldDirty: true,
            shouldValidate: true,
          });
          dispatch(setDestinationLocation(nextLocation));
        })
        .catch(() => {
          // Keep coordinates even if Google cannot resolve an address for them.
        });
    }, GEOCODE_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [destinationLatitude, destinationLongitude, dispatch, form]);

  React.useEffect(() => {
    if (!takeOffLocation || !destinationLocation) return;
    if (!hasValidCoordinatePair(takeOffLocation.latitude, takeOffLocation.longitude)) return;
    if (!hasValidCoordinatePair(destinationLocation.latitude, destinationLocation.longitude)) return;
  
    const path1 = { 
      latitude: Number(takeOffLocation.latitude), 
      longitude: Number(takeOffLocation.longitude),
    };
  
    const path2 = {
      latitude: Number(destinationLocation.latitude), 
      longitude: Number(destinationLocation.longitude),
    };
  
    checkAndPlan({
      isSilent: true,
      path: [path1, path2],  
    });
      
  }, [takeOffLocation, destinationLocation]);

  const errors = form.formState.errors;
  const warningError = error as ApiErrorResponse;

  return (
    <ArcStack height='100%' component='form' onSubmit={form.handleSubmit(onSubmit)}>
      <ArcStack height='100%' justifyContent='space-between'>
        <ArcStack spacing={4}>
          <ArcStack spacing={2}>
            <ArcText.h5> TAKEOFF </ArcText.h5>
            <Controller 
              control={form.control} 
              name='takeOffLocation.address'
              rules={{ required: REQUIRED_VALIDATION_TEXT }}
              render={({ field: { value } }) => (
                <GoogleSearchInput
                  initValue={value} 
                  label='Takeoff Location'
                  readOnly={readOnly}
                  error={Boolean(errors.takeOffLocation?.address)} 
                  helperText={errors.takeOffLocation?.address?.message}
                  onSelect={onChangeTakeOff}
                  endAdornment={
                    <ArcStack>
                      {readOnly 
                        ? (
                          <ArcText.h6 
                            color='brand'
                            sx={{ cursor: 'pointer' }}
                            onClick={() => onChangeReadOnly(false)}> 
                            Edit 
                          </ArcText.h6> 
                        ) : (
                          <ArcText.h6 
                            color='brand' 
                            sx={{ cursor: 'pointer' }}
                            onClick={() => onChangeReadOnly(true)}>
                            Done 
                          </ArcText.h6>
                        )}
                    </ArcStack>
                  } />
              )}
            />
          </ArcStack>

          <ArcStack spacing={2}>
            <ArcText.h5> DESTINATION </ArcText.h5>
            <Controller 
              control={form.control} 
              name='destinationLocation.address' 
              rules={{ required: REQUIRED_VALIDATION_TEXT }}
              render={({ field: { value } }) => (
                <GoogleSearchInput 
                  initValue={value} 
                  label='Delivery Location'
                  error={Boolean(errors.destinationLocation?.address)} 
                  helperText={errors.destinationLocation?.address?.message}
                  onSelect={onChangeDestination} />
              )}
            />
            <ArcStack direction='row' spacing={2}>
              <Controller 
                name='destinationLocation.latitude'
                control={form.control} 
                rules={{
                  required: REQUIRED_VALIDATION_TEXT,
                  validate: (value) => validateCoordinate(value, -90, 90) || 'Invalid latitude',
                }} 
                render={({ field }) => (
                  <ArcOutlinedInput 
                    {...field} 
                    type='text'
                    inputMode='decimal'
                    label='Latitude' 
                    onChange={(e) => onDestinationCoordinateChange('latitude', e.target.value, field.onChange)}
                    error={Boolean(errors?.destinationLocation?.latitude)}
                    helperText={errors?.destinationLocation?.latitude?.message} />
                )} 
              />

              <Controller 
                name='destinationLocation.longitude'
                control={form.control} 
                rules={{
                  required: REQUIRED_VALIDATION_TEXT,
                  validate: (value) => validateCoordinate(value, -180, 180) || 'Invalid longitude',
                }} 
                render={({ field }) => (
                  <ArcOutlinedInput 
                    {...field} 
                    type='text'
                    inputMode='decimal'
                    label='Longitude'
                    onChange={(e) => onDestinationCoordinateChange('longitude', e.target.value, field.onChange)}
                    error={Boolean(errors?.destinationLocation?.longitude)}
                    helperText={errors?.destinationLocation?.longitude?.message} />
                )} 
              />

            </ArcStack>
          </ArcStack>

          {Boolean(warningError) && (
            <ArcAlert severity='warning'>
              <ArcText.h6 color='warning.main'> {warningError.data.message} </ArcText.h6>
            </ArcAlert>
          )}

          <ArcStack spacing={2}>
            <ArcText.h5> DETAILS </ArcText.h5>

            <Controller 
              name='packageContent'
              control={form.control} 
              render={({ field }) => (
                <ArcOutlinedInput 
                  {...field} 
                  label='Package Content'  />
              )} 
            />

            <Controller 
              name='clientName'
              control={form.control} 
              render={({ field }) => (
                <ArcOutlinedInput 
                  {...field} 
                  label='Client Name' />
              )} 
            />
          </ArcStack>
        </ArcStack>

        <ArcStack spacing={1}>
          <ArcStack direction='row' spacing={1}>
            <ArcButton.ghost 
              fullWidth 
              type='button'
              onClick={goBack}
              isLoading={isCanceling}> 
              Cancel 
            </ArcButton.ghost>
            <ArcButton.ghost 
              fullWidth 
              type='button'
              onClick={() => navigate(routesConfig.orders)}
              isLoading={isSubmitting}> 
              Exit 
            </ArcButton.ghost>
          </ArcStack>
          <ArcButton.brand 
            fullWidth 
            type='submit' 
            isLoading={isSubmitting}
            disabled={Boolean(warningError)}> 
            Next 
          </ArcButton.brand>
        </ArcStack>
      </ArcStack>
    </ArcStack>
  );
};

export default OrderForm;